/**
 * File size formatting utilities.
 * Converts bytes and bits to human-readable formats.
 *
 * @module file-size-formatter
 */

type UnitType = 'rate' | 'quantity';

/**
 * Format bytes to a human-readable string.
 * @param value - The value in bytes
 * @param unitType - 'quantity' for storage (B, KB, MB) or 'rate' for speed (Bps, KBps, MBps)
 * @returns Formatted string
 *
 * @example
 * toReadableBytes(1500000, 'quantity') // '1.50 MB'
 * toReadableBytes(1500000, 'rate') // '1.50 MBps'
 */
export function toReadableBytes(value: number, unitType: UnitType): string {
    const unitBase = unitType === 'quantity' ? 'B' : 'Bps';
    const n = Math.floor(value);

    if (n >= 1_000_000_000) {
        return `${(n / 1_000_000_000).toFixed(2)} G${unitBase}`;
    }
    if (n >= 1_000_000) {
        return `${(n / 1_000_000).toFixed(2)} M${unitBase}`;
    }
    if (n >= 1_000) {
        return `${(n / 1_000).toFixed(2)} K${unitBase}`;
    }

    return unitType === 'quantity' ? `${n} Bytes` : `${n} Bps`;
}

/**
 * Format bytes compactly at ~3 significant digits with no trailing-zero
 * padding — for estimates and derived values where "107.70 MB" would be
 * dishonest precision: 107.7e6 -> '108 MB', 22.58e6 -> '22.6 MB',
 * 5.25e6 -> '5.25 MB', 748e3 -> '748 KB'.
 */
export function toCompactReadableBytes(value: number): string {
    const n = Math.floor(value);
    const format = (v: number): string => {
        const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
        return parseFloat(v.toFixed(digits)).toString();
    };
    if (n >= 1_000_000_000) {
        return `${format(n / 1_000_000_000)} GB`;
    }
    if (n >= 1_000_000) {
        return `${format(n / 1_000_000)} MB`;
    }
    if (n >= 1_000) {
        return `${format(n / 1_000)} KB`;
    }
    return `${n} Bytes`;
}

/**
 * Format bits to a human-readable string.
 * @param bits - The value in bits
 * @param unitType - 'quantity' for storage (b, Kb, Mb) or 'rate' for speed (bps, Kbps, Mbps)
 * @returns Formatted string
 *
 * @example
 * toReadableBits(1500000, 'quantity') // '1.50 Mb'
 * toReadableBits(1500000, 'rate') // '1.50 Mbps'
 */
export function toReadableBits(bits: number, unitType: UnitType): string {
    const unitBase = unitType === 'quantity' ? 'b' : 'bps';
    const n = Math.floor(bits);

    if (n >= 1_000_000_000) {
        return `${(n / 1_000_000_000).toFixed(2)} G${unitBase}`;
    }
    if (n >= 1_000_000) {
        return `${(n / 1_000_000).toFixed(2)} M${unitBase}`;
    }
    if (n >= 1_000) {
        return `${(n / 1_000).toFixed(2)} K${unitBase}`;
    }

    return unitType === 'quantity' ? `${n} bits` : `${n} bps`;
}

/**
 * Convert bytes to megabytes.
 * @param bytes - The value in bytes
 * @returns Value in megabytes
 */
export function bytesToMB(bytes: number): number {
    return bytes / 1_000_000;
}

/**
 * Convert megabytes to bytes.
 * @param mb - The value in megabytes
 * @returns Value in bytes
 */
export function mbToBytes(mb: number): number {
    return mb * 1_000_000;
}

/**
 * Convert bytes to gigabytes.
 * @param bytes - The value in bytes
 * @returns Value in gigabytes
 */
export function bytesToGB(bytes: number): number {
    return bytes / 1_000_000_000;
}

/**
 * Convert gigabytes to bytes.
 * @param gb - The value in gigabytes
 * @returns Value in bytes
 */
export function gbToBytes(gb: number): number {
    return gb * 1_000_000_000;
}

/**
 * Format file size with automatic unit selection.
 *
 * Decimal (SI) units, consistent with every other helper in this module and with the
 * backend's size targets (10 MB = 10,000,000 B). This previously divided by 1024 while
 * labelling the result KB/MB/GB — binary maths wearing decimal labels — which made the
 * same file report two different sizes depending on which screen rendered it.
 *
 * @param bytes - The file size in bytes
 * @returns Formatted string with appropriate unit
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1000;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
